<template>
  <div>
    <a-card :loading="loading">
      <a-page-header :title="reviewData.title" :sub-title="`评审单详情 #${reviewData.key}`" @back="toReviewList">
        <template slot="extra">
          <a-button v-if="hasSignPermission" v-tc="{ name: '通过' }" type="primary" class="pass-btn" size="small" @click="onReviewPass"> 通过</a-button>
          <a-button v-if="hasSignPermission" v-tc="{ name: '不通过' }" type="danger" size="small" @click="reviewFailModalVisible = true">不通过</a-button>
          <a-dropdown>
            <a-menu slot="overlay">
              <a-menu-item key="1" v-tc="{ name: '编辑' }" @click="onEdit"> 编辑</a-menu-item>
              <a-menu-item key="2" v-tc="{ name: '复制' }" @click="onCopy(reviewId)"> 复制</a-menu-item>
              <a-menu-item v-if="reviewData.is_locked === 0" key="4" v-tc="{ name: '归档' }" @click="onLock(reviewId)"> 归档</a-menu-item>
              <a-menu-item v-if="reviewData.is_locked === 1" key="4" :disabled="reviewData.is_locked"> 已归档</a-menu-item>
              <a-menu-item key="3" v-tc="{ name: '删除' }" :disabled="!reviewData.has_delete_permission" @click="onDelete(reviewId)">删除</a-menu-item>
            </a-menu>
            <a-button size="small">
              更多
              <a-icon type="down" />
            </a-button>
          </a-dropdown>
        </template>
        <a-descriptions size="small" :column="3">
          <a-descriptions-item label="当前状态">
            <review-status-edit :review-id="reviewData.id" :next-status="reviewData.next_status" :status="reviewData.status" @change="getReviewDetail" />
          </a-descriptions-item>
          <a-descriptions-item label="评审类型">
            <a-badge v-if="reviewData.review_scope !== null && reviewTypes[reviewData.review_scope]" :color="reviewTypes[reviewData.review_scope].color" :text="reviewTypes[reviewData.review_scope].label" />
          </a-descriptions-item>
          <a-descriptions-item label="参与人">
            <user-profiles :value="reviewData.reviewers.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="计划评审时间"> {{ reviewData.review_time || '--' }}</a-descriptions-item>
          <a-descriptions-item label="评审进度">
            <a-progress :percent="reviewData.progress" size="small" style="width: 150px" />
          </a-descriptions-item>
          <a-descriptions-item label="已评审/总数">
            <span>{{ reviewData.finished_items }}/{{ reviewData.total_items }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="创建人"><user-profiles v-if="reviewData.creator" :value="[reviewData.creator.id]" avatar /></a-descriptions-item>
          <a-descriptions-item label="创建时间"> {{ reviewData.created_time }}</a-descriptions-item>
          <a-descriptions-item label="更新时间"> {{ reviewData.updated_time }}</a-descriptions-item>
        </a-descriptions>
      </a-page-header>
      <a-tabs v-model="reviewTabsActive" @change="handleTabChange">
        <a-tab-pane key="1" tab="详细信息">
          <div style="background: #fafafa; padding: 16px">
            <vditor-preview :content="reviewData.description" />
          </div>
        </a-tab-pane>
        <a-tab-pane v-if="reviewData.review_scope === 2" key="2" tab="关联用例" force-render>
          <review-case-list ref="caseList" :review-id="reviewData.id" @add-case="onAddCase" @refresh="getReviewDetail" />
        </a-tab-pane>
        <a-tab-pane v-if="reviewData.review_scope === 1" key="3" tab="关联需求" force-render>
          <review-story-list ref="storyList" :review-id="reviewData.id" @add-story="onAddStory" @refresh="getReviewDetail" />
        </a-tab-pane>
        <a-tab-pane v-if="reviewData.review_scope === 4" key="4" tab="关联任务" force-render>
          <review-task-list ref="taskList" :review-id="reviewData.id" @add-story="onAddTask" @refresh="getReviewDetail" />
        </a-tab-pane>
        <a-tab-pane key="5" tab="评审任务" force-render>
          <a-table :pagination="false" :columns="columns" :data-source="reviewData.sign_list" size="middle" row-key="index">
            <span slot="number" slot-scope="record">{{ reviewData.sign_list.length + 1 - record.index }}</span>
            <span slot="reviewer" slot-scope="reviewer">{{ reviewer ? reviewer.nickname : '系统' }}</span>
            <span slot="result" slot-scope="result">
              <a-badge v-if="reviewStatus[result]" :status="reviewStatus[result].status" :text="reviewStatus[result].label" />
            </span>
            <span slot="description" slot-scope="description">{{ description ? description : '--' }}</span>
            <span slot="signWay">会签</span>
          </a-table>
        </a-tab-pane>
        <a-tab-pane key="6" tab="变更历史" force-render>
          <review-record ref="record" :review-id="reviewData.id" />
        </a-tab-pane>
      </a-tabs>
      <CommentPanel mode="review" :active-key="activeKey" :source-id="reviewData.id" />
    </a-card>
    <review-modal :is-show="modalVisible" :review-id="reviewId" @close="close" />
    <case-selector :is-show="caseSelectorVisible" :plan-bind-keys="reviewBindCases" :ok-loading="okLoading" @close="closeSelector" />
    <story-selector :is-show="storySelectorVisible" :data="storyData" :plan-bind-keys="reviewBindStories" :total="storyTotal" @close="closeStorySelector" @change="pageChange" @search="getStoryList" />
    <a-modal title="评审不通过" :visible="reviewFailModalVisible" @ok="onReviewFail" @cancel="handleCancel">
      <a-form-model :form="failForm" :model="failForm" :rules="rules" class="search-form">
        <a-form-model-item label="备注" prop="failReason">
          <a-textarea v-model="failForm.failReason" placeholder="评审不通过备注" :auto-size="{ minRows: 3, maxRows: 3 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { moduleList } from '@/api/project/case';
import { storyTree } from '@/api/project/story';
import CommentPanel from '@/components/comment/CommentPanel';
import VditorPreview from '@/components/VditorEditor/preview';
import { reviewTypes, reviewStatus } from '@/utils/dictionary';
import ReviewModal from '@/pages/project/review/components/ReviewModal';
import ReviewRecord from '@/pages/project/review/components/ReviewRecord';
import CaseSelector from '@/pages/project/testplan/components/CaseSelector';
import StorySelector from '@/pages/project/review/components/StorySelector';
import ReviewCaseList from '@/pages/project/review/components/ReviewCaseList';
import ReviewTaskList from '@/pages/project/review/components/ReviewTaskList';
import ReviewStoryList from '@/pages/project/review/components/ReviewStoryList';
import ReviewStatusEdit from '@/pages/project/review/components/ReviewStatusEdit';
import { bindStories, bindTestCases, reviewBindCaseIds, reviewBindStoryIds, reviewCopy, reviewDelete, reviewDetail, reviewSign, reviewLock } from '@/api/project/review';

const columns = [
  {
    title: '序号',
    width: '2%',
    align: 'center',
    scopedSlots: { customRender: 'number' }
  },
  {
    title: '评审人',
    width: '5%',
    align: 'center',
    dataIndex: 'reviewer',
    key: 'reviewer',
    scopedSlots: { customRender: 'reviewer' }
  },
  {
    title: '评审状态',
    width: '5%',
    align: 'center',
    dataIndex: 'result',
    key: 'result',
    scopedSlots: { customRender: 'result' }
  },
  {
    title: '签名方式',
    width: '5%',
    align: 'center',
    scopedSlots: { customRender: 'signWay' }
  },
  {
    title: '备注',
    width: '10%',
    align: 'center',
    dataIndex: 'description',
    key: 'description',
    scopedSlots: { customRender: 'description' }
  },
  {
    title: '操作时间',
    width: '5%',
    align: 'center',
    dataIndex: 'review_time',
    key: 'review_time'
    // scopedSlots: {customRender: 'reviewer'}
  }
];
export default {
  name: 'Detail',
  components: {
    ReviewModal,
    CommentPanel,
    ReviewRecord,
    CaseSelector,
    StorySelector,
    VditorPreview,
    ReviewCaseList,
    ReviewTaskList,
    ReviewStoryList,
    ReviewStatusEdit
  },
  data() {
    return {
      columns,
      reviewTypes,
      reviewStatus,
      loading: true,
      reviewTabsActive: '1',
      reviewData: {
        id: null,
        reviewers: []
      },
      editReviewId: null,
      modalVisible: false,
      projectId: parseInt(this.$route.params.project_id) || null,
      reviewId: parseInt(this.$route.query.reviewId) || null,
      activeKey: [],
      reviewBindCases: [],
      reviewBindStories: [],
      caseSelectorVisible: false,
      storySelectorVisible: false,
      caseData: [],
      storyData: [],
      issueList: [],
      issueListLoading: false,
      hasSignPermission: false,
      reviewFailModalVisible: false,
      failForm: {
        failReason: ''
      },
      rules: {
        failReason: [{ required: true, message: '评审不通过备注不能为空', trigger: 'blur' }]
      },
      storyTotal: 0,
      okLoading: false,
      tabItems: [
        { key: '1', label: '详细信息' },
        { key: '2', label: '关联用例' },
        { key: '3', label: '关联需求' },
        { key: '4', label: '关联任务' },
        { key: '5', label: '评审任务' },
        { key: '6', label: '变更历史' }
      ]
    };
  },
  computed: {
    reviewers() {
      return record => {
        return record.reviewers || [];
      };
    }
  },
  created() {
    if (this.reviewId !== null) {
      this.getReviewDetail();
      this.getStoryList();
    } else {
      this.$router.push({ name: 'page:project:review:list' });
    }
  },
  methods: {
    handleTabChange(activeKey) {
      const item = this.tabItems.find(item => item.key === activeKey);
      this.utils.trackClick({ name: '切换卡片', label: item ? item.label : activeKey });
      if (activeKey === '5') {
        this.$refs.record.getReviewRecord();
      }
    },
    getCaseList() {
      moduleList({ project_id: this.projectId, detailed: true }).then(res => {
        this.moduleCases = res.data.module_cases;
        const items = res.data.items.map(item => {
          return {
            id: item.id,
            title: item.title,
            key: 'module-' + item.key,
            children: [],
            parent_id: item.parent_id
          };
        });
        const caseData = [
          {
            id: 0,
            key: 'module-0',
            title: '未分配',
            children: this.moduleCases['null']
          },
          ...this.listToTree(items)
        ];
        // this.caseData = handleTreeData(caseData);
        this.caseData = caseData;
      });
    },
    listToTree(list, parentId = 0) {
      const menuObj = {};
      const tempArr = [];
      list.forEach(item => {
        const temp = {
          ...item,
          children: this.moduleCases[item.id.toString()] || []
        };
        tempArr.push(temp);
        menuObj[item.id] = temp;
      });

      return tempArr.filter(item => {
        if (item.parent_id !== parentId) {
          menuObj[item.parent_id].children.push(item);
          return false;
        }
        return true;
      });
    },
    onDelete(id) {
      const self = this;
      this.$confirm({
        title: '确认',
        content: '确认要删除该评审单吗',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        async onOk() {
          reviewDelete({ project_id: self.projectId, review_id: id }).then(() => {
            self.$message.success('删除成功');
            self.$router.push({ name: 'page:project:review:list' });
          });
        }
      });
    },
    onCopy(reviewId) {
      reviewCopy({ review_id: reviewId }).then(() => {
        this.$message.success('复制评审单成功');
      });
    },
    onLock(reviewId) {
      reviewLock({ review_id: reviewId, project_id: this.projectId }).then(() => {
        this.$message.success('归档成功');
        this.getPlanList();
      });
    },
    /**
     * 添加关联用例
     */
    onAddCase() {
      reviewBindCaseIds({ review_id: this.reviewId }).then(res => {
        this.reviewBindCases = res.data.items;
        this.caseSelectorVisible = true;
      });
    },
    /**
     * 添加关联需求
     */
    onAddStory() {
      reviewBindStoryIds({ review_id: this.reviewId }).then(res => {
        this.reviewBindStories = res.data.items;
        this.storySelectorVisible = true;
      });
    },
    /**
     * 添加关联任务
     */
    onAddTask() {},
    closeSelector(targetKeys = null) {
      if (targetKeys !== null) {
        this.bindCases(targetKeys);
      } else {
        this.caseSelectorVisible = false;
      }
    },

    bindStories(stories) {
      bindStories({ review_id: this.reviewId, stories: stories })
        .then(() => {
          this.storySelectorVisible = false;
          this.$message.success('关联需求成功');
          this.$refs.storyList.getStoryList();
          this.getReviewDetail();
        })
        .catch(() => {
          this.storySelectorVisible = false;
        });
    },
    closeStorySelector(targetKeys = null) {
      if (targetKeys !== null) {
        this.bindStories(targetKeys);
      } else {
        this.storySelectorVisible = false;
      }
    },
    bindCases(testCases) {
      this.okLoading = true;
      bindTestCases({ review_id: this.reviewId, cases: testCases })
        .then(() => {
          this.okLoading = false;
          this.caseSelectorVisible = false;
          this.$message.success('关联用例成功');
          this.$refs.caseList.getCaseList();
          this.getReviewDetail();
        })
        .catch(() => {
          this.okLoading = false;
          this.caseSelectorVisible = false;
        });
    },
    toReviewList() {
      this.$router.push({ name: 'page:project:review:list' });
    },
    // 点击编辑
    onEdit() {
      this.editReviewId = this.reviewData.id;
      this.modalVisible = true;
    },
    onReviewPass() {
      reviewSign({ review_id: this.reviewId, result: 3 }).then(() => {
        this.$message.success('评审(通过)成功');
        this.getReviewDetail();
      });
    },
    handleCancel() {
      this.reviewFailModalVisible = false;
    },
    onReviewFail() {
      if (!this.failReason) {
        this.$message.warning('请填写不通过原因');
        return;
      }
      reviewSign({ review_id: this.reviewId, result: 4, description: this.failReason }).then(() => {
        this.reviewFailModalVisible = false;
        this.$message.success('评审(不通过)成功');
        this.getReviewDetail();
      });
    },
    getReviewDetail() {
      this.loading = true;
      reviewDetail({ review_id: this.reviewId, project_id: this.projectId }).then(res => {
        this.reviewData = res.data;
        this.activeKey = [];
        this.activeKey.push('1');
        this.loading = false;
        this.hasSignPermission = res.data.has_sign_permission;
      });
    },
    close() {
      this.modalVisible = false;
      this.getReviewDetail();
    },
    getStoryList(page_number = 1, title = null) {
      if (title === '') {
        title = null;
      }
      this.storyData = [];
      this.expendedKeys = [];
      storyTree({ project_id: this.projectId, page_number: page_number, page_size: 10, title: title }).then(res => {
        this.storyData = res.data.items;
        this.expendedKeys = res.data.requirement_ids;
        this.storyTotal = res.data.total;
      });
    },
    pageChange(page_number = 1) {
      this.getStoryList(page_number);
    }
  }
};
</script>

<style lang="less" scoped>
/deep/ .ant-card-body {
  padding: 16px;
}

/deep/ .ant-page-header {
  padding: 0;
}

.pass-btn {
  color: #fff;
  background-color: #3ac27d;
  border-color: #3ac27d;
}

.fail-btn {
  color: #fff;
  background-color: #e95409;
  border-color: #e95409;
  //margin-right: 10px
}
</style>
